/**
 * Landing Page
 *
 * Product landing page with sign-in CTA. Displayed when user is not authenticated.
 * Sections: Hero, Dual Value Prop, How It Works, Features, Developer API, Final CTA.
 */

import { useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Link as LinkIcon,
  FileText,
  Shield,
  Newspaper,
  Tag,
  Building2,
  Mic,
  Code2,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// ============================================
// GOOGLE ICON
// ============================================

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// ============================================
// SIGN IN BUTTON
// ============================================

function SignInButton({
  isSigningIn,
  onSignIn,
  size = 'lg',
}: {
  isSigningIn: boolean;
  onSignIn: () => void;
  size?: 'lg' | 'md';
}) {
  return (
    <button
      onClick={onSignIn}
      disabled={isSigningIn}
      className="inline-flex items-center justify-center font-medium rounded-lg border transition-all duration-200 hover:shadow-md active:scale-[0.98]"
      style={{
        background: 'var(--bg-primary)',
        borderColor: 'var(--border-primary)',
        color: 'var(--text-secondary)',
        height: size === 'lg' ? '48px' : '40px',
        padding: size === 'lg' ? '0 24px' : '0 16px',
        fontSize: size === 'lg' ? '15px' : '14px',
        opacity: isSigningIn ? 0.7 : 1,
      }}
    >
      {isSigningIn ? (
        <>
          <Loader2 className="h-5 w-5 mr-3 animate-spin" />
          Signing in...
        </>
      ) : (
        <>
          <GoogleIcon className="h-5 w-5 mr-3" />
          Sign in with Google
        </>
      )}
    </button>
  );
}

// ============================================
// LANDING PAGE
// ============================================

export function SignIn() {
  const { signIn, isLoading, error, clearError } = useAuth();
  const [localLoading, setLocalLoading] = useState(false);

  const handleSignIn = async () => {
    setLocalLoading(true);
    clearError();
    try {
      await signIn();
    } catch {
      // Error handled by auth context
    } finally {
      setLocalLoading(false);
    }
  };

  const isSigningIn = isLoading || localLoading;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* ---- Top Nav ---- */}
      <nav className="flex items-center justify-between px-4 sm:px-6 py-4 max-w-6xl mx-auto">
        <span
          className="font-serif text-lg sm:text-xl font-bold"
          style={{ color: 'var(--text-primary)' }}
        >
          Personal Insights
        </span>
        <div className="flex items-center gap-2 sm:gap-4">
          <a
            href="/privacy"
            className="type-label hover:underline hidden sm:inline"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Privacy
          </a>
          <SignInButton isSigningIn={isSigningIn} onSignIn={handleSignIn} size="md" />
        </div>
      </nav>

      {/* ---- Error Banner ---- */}
      {error && (
        <div className="max-w-6xl mx-auto px-6 mb-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* ---- Hero ---- */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <div>
            <p
              className="type-overline mb-4"
              style={{ color: 'var(--accent-primary)', letterSpacing: '0.1em' }}
            >
              Private by default
            </p>
            <h1
              className="font-serif font-bold leading-tight mb-5"
              style={{ color: 'var(--text-primary)', fontSize: 'clamp(28px, 4vw, 40px)' }}
            >
              Your private workspace. The tech news worth your time.
            </h1>
            <p
              className="type-body mb-8 leading-relaxed"
              style={{ color: 'var(--text-secondary)', fontSize: '16px', maxWidth: '480px' }}
            >
              Save links, notes, and ideas to your Google Drive. Read a curated feed from 40+ tech
              and investor sources — each with a clear verdict and entity tags.
            </p>
            <div className="mb-6">
              <SignInButton isSigningIn={isSigningIn} onSignIn={handleSignIn} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <span
                className="type-caption flex items-center gap-1.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                <Shield className="h-3.5 w-3.5" /> Stored in your Google Drive
              </span>
              <span
                className="type-caption flex items-center gap-1.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                <Newspaper className="h-3.5 w-3.5" /> 40+ sources tracked
              </span>
              <span
                className="type-caption flex items-center gap-1.5"
                style={{ color: 'var(--text-tertiary)' }}
              >
                <Code2 className="h-3.5 w-3.5" /> Developer API for agents
              </span>
            </div>
          </div>
          {/* Product Preview */}
          <div
            className="rounded-xl overflow-hidden shadow-lg border"
            style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-secondary)' }}
          >
            <div
              className="px-4 py-2 flex items-center gap-2 border-b"
              style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-tertiary)' }}
            >
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
              <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
                personalinsights.app/feed
              </span>
            </div>
            <div className="p-4 space-y-3">
              {/* Mock feed cards */}
              {[
                {
                  verdict: 'must_read',
                  title: 'Kleiner Perkins Raises $3.5B for AI',
                  topics: ['AI', 'Fundraising'],
                  entity: '@Kleiner Perkins',
                },
                {
                  verdict: 'worth_reading',
                  title: 'Arm Selling Own Chips, Meta First Customer',
                  topics: ['Semiconductors', 'Hardware'],
                  entity: '$ARM',
                },
                {
                  verdict: 'worth_reading',
                  title: 'SpaceX Could File IPO by End of Week',
                  topics: ['IPO'],
                  entity: '@SpaceX',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg border"
                  style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-primary)' }}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="type-caption px-1.5 py-0.5 rounded-full"
                      style={{
                        background: 'rgba(35, 131, 226, 0.12)',
                        color: 'var(--accent-primary)',
                        fontSize: '10px',
                        fontWeight: 500,
                      }}
                    >
                      {item.verdict === 'must_read' ? 'Must Read' : 'Worth Reading'}
                    </span>
                    <span
                      className="type-caption"
                      style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}
                    >
                      {item.entity}
                    </span>
                  </div>
                  <p
                    className="font-serif text-sm font-semibold"
                    style={{ color: 'var(--text-primary)', lineHeight: '1.3' }}
                  >
                    {item.title}
                  </p>
                  <div className="flex gap-1.5 mt-1.5">
                    {item.topics.map((t) => (
                      <span
                        key={t}
                        className="type-caption px-1.5 py-0.5 rounded"
                        style={{
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-tertiary)',
                          fontSize: '10px',
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- Dual Value Prop ---- */}
      <section style={{ background: 'var(--bg-secondary)' }}>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2
            className="font-serif text-center font-bold mb-12"
            style={{ color: 'var(--text-primary)', fontSize: '28px' }}
          >
            Keep what you learn. Keep up with what matters.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div
              className="p-6 rounded-xl border"
              style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-primary)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5" style={{ color: 'var(--accent-primary)' }} />
                <h3 className="type-label font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Your private workspace
                </h3>
              </div>
              <p
                className="type-body"
                style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
              >
                Save links, write notes, capture ideas, and track tasks — all stored in your Google
                Drive, not our servers. Search across everything. Works on any device.
              </p>
            </div>
            <div
              className="p-6 rounded-xl border"
              style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-primary)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Newspaper className="h-5 w-5" style={{ color: 'var(--accent-primary)' }} />
                <h3 className="type-label font-semibold" style={{ color: 'var(--text-primary)' }}>
                  A feed that does the sorting
                </h3>
              </div>
              <p
                className="type-body"
                style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
              >
                Follow the most relevant tech and investor stories from 40+ curated sources. Each
                article gets a verdict, topic tags, and company mentions — so you read less and know
                more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---- How It Works ---- */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2
          className="font-serif text-center font-bold mb-12"
          style={{ color: 'var(--text-primary)', fontSize: '28px' }}
        >
          How it works
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              step: '1',
              title: 'Save in seconds.',
              desc: 'Drop in a link, note, idea, or task. Everything goes straight to your Google Drive.',
            },
            {
              step: '2',
              title: 'Read less, know more.',
              desc: 'Open a curated feed instead of checking every source yourself. Clear verdicts tell you what matters.',
            },
            {
              step: '3',
              title: 'Go deeper.',
              desc: 'Track companies, explore entities, or connect the API to your own agent workflows.',
            },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 font-serif font-bold"
                style={{
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  fontSize: '18px',
                }}
              >
                {item.step}
              </div>
              <h3
                className="type-label font-semibold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {item.title}
              </h3>
              <p
                className="type-body"
                style={{ color: 'var(--text-secondary)', maxWidth: '280px', margin: '0 auto' }}
              >
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Feature Grid ---- */}
      <section style={{ background: 'var(--bg-secondary)' }}>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2
            className="font-serif text-center font-bold mb-12"
            style={{ color: 'var(--text-primary)', fontSize: '28px' }}
          >
            Everything you need
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: <LinkIcon className="h-4.5 w-4.5" />,
                title: 'Links, notes, ideas, and tasks',
                desc: 'All your saved knowledge in one place.',
              },
              {
                icon: <Tag className="h-4.5 w-4.5" />,
                title: 'Clear verdicts on every story',
                desc: 'Know instantly what to read, what to skim, and what to skip.',
              },
              {
                icon: <Building2 className="h-4.5 w-4.5" />,
                title: 'Companies and tickers tagged',
                desc: 'See which companies matter in each article.',
              },
              {
                icon: <Newspaper className="h-4.5 w-4.5" />,
                title: '20 investment topics',
                desc: 'AI, M&A, Fundraising, Semiconductors, and more.',
              },
              {
                icon: <Mic className="h-4.5 w-4.5" />,
                title: 'Voice-to-note',
                desc: 'Speak your thoughts. Works on Chrome and Edge.',
              },
              {
                icon: <Shield className="h-4.5 w-4.5" />,
                title: 'Private by default',
                desc: 'Your saved items live in your Google Drive.',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="p-4 rounded-lg border"
                style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-primary)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ color: 'var(--accent-primary)' }}>{f.icon}</span>
                  <h3 className="type-label font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {f.title}
                  </h3>
                </div>
                <p className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Developer API ---- */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p
              className="type-overline mb-3"
              style={{ color: 'var(--accent-primary)', letterSpacing: '0.1em' }}
            >
              Developer API
            </p>
            <h2
              className="font-serif font-bold mb-4"
              style={{ color: 'var(--text-primary)', fontSize: '28px' }}
            >
              Bring the feed into your own tools.
            </h2>
            <p
              className="type-body mb-6"
              style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}
            >
              The Developer API lets agents read curated articles, verdicts, and entities. Efficient
              polling — only fetch what's new. Starts free, $10/mo for full access.
            </p>
            <a
              href="/settings"
              className="type-label inline-flex items-center gap-1.5 hover:underline"
              style={{ color: 'var(--accent-primary)' }}
            >
              Get an API key <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <div
            className="p-4 rounded-xl border font-mono text-sm"
            style={{
              borderColor: 'var(--border-primary)',
              background: '#1e1e1e',
              color: '#d4d4d4',
              lineHeight: '1.7',
            }}
          >
            <div style={{ color: '#6a9955' }}>{'# Get your curated feed'}</div>
            <div>
              <span style={{ color: '#dcdcaa' }}>curl</span>{' '}
              <span style={{ color: '#ce9178' }}>https://personalinsights.app/api/v1/feed</span>{' '}
              <span style={{ color: '#569cd6' }}>\</span>
            </div>
            <div>
              {'  '}
              <span style={{ color: '#569cd6' }}>-H</span>{' '}
              <span style={{ color: '#ce9178' }}>&quot;Authorization: Bearer YOUR_KEY&quot;</span>
            </div>
            <div className="mt-3" style={{ color: '#6a9955' }}>
              {'# Poll for new items'}
            </div>
            <div>
              <span style={{ color: '#dcdcaa' }}>curl</span>{' '}
              <span style={{ color: '#ce9178' }}>.../api/v1/feed/updates?cursor=...</span>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Final CTA ---- */}
      <section className="text-center py-20" style={{ background: 'var(--bg-secondary)' }}>
        <h2
          className="font-serif font-bold mb-3"
          style={{ color: 'var(--text-primary)', fontSize: '28px' }}
        >
          Start with Google. Keep your workspace private.
        </h2>
        <p className="type-body mb-6" style={{ color: 'var(--text-tertiary)' }}>
          No separate account to manage.
        </p>
        <SignInButton isSigningIn={isSigningIn} onSignIn={handleSignIn} />
      </section>

      {/* ---- Footer ---- */}
      <footer className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
          Your saved items live in your Google Drive.
        </p>
        <div className="flex items-center gap-4">
          <a
            href="/privacy"
            className="type-caption hover:underline"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Privacy Policy
          </a>
          <a
            href="mailto:hello@insight-os.ai"
            className="type-caption hover:underline"
            style={{ color: 'var(--text-tertiary)' }}
          >
            hello@insight-os.ai
          </a>
        </div>
      </footer>
    </div>
  );
}
